import eventBus from '../service/eventBus';
import { AD_CLICK_EVENT, AD_CTA_CLICK_EVENT } from './constants';
import SessionService from '../service/sessionService';
import AnalyticsService from '../service/analyticsService';
import { EVENT_AD_CLICKED, EVENT_AD_CTA_CLICKED } from './analyticsConstants';
import { hasActiveProSubscription } from '../service/billingService';

/**
 * Utility functions for ad management and tracking
 */

/**
 * Track ad click for analytics
 * @param {string} adId - Unique identifier for the ad
 * @param {string} title - Ad title
 * @param {Object} additionalData - Any additional data to track
 */
export const trackAdClick = (adId, title, additionalData = {}) => {
    const trackingData = {
        adId,
        title,
        timestamp: Date.now(),
        ...additionalData
    };
    AnalyticsService.getInstance().track(EVENT_AD_CLICKED, { adId, title });
    console.log('Ad click tracked:', trackingData);
    eventBus.emit(AD_CLICK_EVENT, trackingData);
};

/**
 * Track ad CTA click for analytics
 * @param {string} adId - Unique identifier for the ad
 * @param {string} title - Ad title
 * @param {string} ctaText - CTA button text
 * @param {Object} additionalData - Any additional data to track
 */
export const trackAdCtaClick = (adId, title, ctaText, additionalData = {}) => {
    const trackingData = {
        adId,
        title,
        ctaText,
        timestamp: Date.now(),
        ...additionalData
    };
    AnalyticsService.getInstance().track(EVENT_AD_CTA_CLICKED, { adId, title, ctaText });
    console.log('Ad CTA click tracked:', trackingData);
    eventBus.emit(AD_CTA_CLICK_EVENT, trackingData);
};

/**
 * Create ad card data object
 * @param {number} adIndex - Index for the ad
 * @param {Object} customData - Custom ad data to override defaults
 * @returns {Object} Ad card data object
 */
export const createAdCardData = (adIndex, customData = {}) => {
    const defaultAdData = {
        type: 'ad',
        adId: `ad-${adIndex}`,
        title: 'StatSpyder Pro',
        description: 'Unlock unlimited scouting reports with no ads.',
        ctaText: 'Upgrade Now',
        sponsoredLabel: true,
        adImageUrl: null,
        indexId: `ad-${adIndex}`
    };
    
    return {
        ...defaultAdData,
        ...customData
    };
};

/**
 * Check if user should see ads (for pro users, etc.)
 * @param {Object} userData - User data object
 * @returns {boolean} Whether ads should be shown
 */
export const shouldShowAds = (userData) => {
    return !hasActiveProSubscription(SessionService.getInstance().getPaymentSubscription());
};

/**
 * Get ad injection positions for a given number of bet cards
 * @param {number} betCardCount - Number of bet cards
 * @param {number} injectionInterval - Interval between ad injections
 * @returns {Array} Array of positions where ads should be injected
 */
export const getAdInjectionPositions = (betCardCount, injectionInterval = 5) => {
    const positions = [];
    for (let i = injectionInterval; i <= betCardCount; i += injectionInterval) {
        positions.push(i);
    }
    return positions;
};

/**
 * Inject ads into bet card array
 * @param {Array} betCards - Array of bet cards
 * @param {number} injectionInterval - Interval between ad injections
 * @param {Function} adCardCreator - Function to create ad card data
 * @returns {Array} Array with bet cards and injected ads
 */
export const injectAdsIntoBetCards = (betCards, injectionInterval = 5, adCardCreator) => {
    const feedWithAds = [];
    let adIndex = 1;

    betCards.forEach((card, index) => {
        // Add bet card
        feedWithAds.push(card);
        
        // Inject ad at specified intervals
        if ((index + 1) % injectionInterval === 0) {
            const adCard = adCardCreator ? adCardCreator(adIndex) : createAdCardData(adIndex);
            feedWithAds.push(adCard);
            adIndex++;
        }
    });

    return feedWithAds;
};

const mlEnabled = SessionService.getInstance().getUserSettings()?.tier?.features?.ml_insights; 
